import { Fragment, useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import Ad from '@/components//Ad';
import AdTop from '@/components//AdTop';
import ButtonSort from '@/components//ButtonSort';
import Loader from '@/components//Loader';
import Note from '@/components//Note';
import ButtonLoadMoreNotes from '@/components/ButtonLoadMoreNotes';
import ButtonShowNewNotes from '@/components/ButtonShowNewNotes';
import LiveSportScoreboard from '@/components/LiveSportScoreboard';
import LiveStatusLabel from '@/components/LiveStatusLabel';
import Timeline from '@/components/Timeline';
import { useAugmentedState } from '@/hooks/useAugmentedState';
import { useLiveType } from '@/hooks/useLiveType';
import { usePageData } from '@/hooks/usePageData';
import { useTestMode } from '@/hooks/useTestMode';
import { craftFetchURL, fetchNotesAfterData, fetchNotesBeforeData } from '@/services/api';
import { convertNoteToTimelineItem } from '@/services/timeline';
import { deleteNote, findNote, prepareData, updateNotes, WSFrame } from '@/services/websockets';
import { type IMatchHeader, type INote, type INotesSortingOrder, type IScoreboard, type ITimelineItem } from '@/types/INote';
import styles from './index.module.scss';

const NOTES_LIMIT_PER_REQUEST = 20;
const SHOW_ADVERT_EVERY_NUMBER_OF_NOTES = 3;
const WEBSOCKET_URL = (() => {
  const url = craftFetchURL({ endpoint: '/ws-live-gazeta' });
  url.protocol = 'wss:';
  return url.toString();
})();

const scrollToElementWithOffset = ({ behavior = 'instant', id, offset }: { behavior?: ScrollBehavior; id: string; offset: number; }) => {
  requestAnimationFrame(() => {
    const el = document.getElementById(id);
    if (el) {
      const y = window.scrollY + el.getBoundingClientRect().top + offset;
      window.scrollTo({ behavior, top: Math.max(0, y) });
    }
  });
};

export default function Rtc () {
  const { articleId } = usePageData();
  const { testArticleId } = useTestMode();
  const { liveType } = useLiveType();

  const xx = testArticleId || articleId;
  const isSportLive = useMemo(() => liveType === 'SPORT', [liveType]);

  const [shallShowLoader, setShallShowLoader] = useState(false);
  const [shallLoadMoreNotes, setShallLoadMoreNotes] = useState(false);
  const [notes, setNotes, notesReference] = useAugmentedState<INote[]>([]);

  const [timelineItems, setTimelineItems, timelineItemsReference] = useAugmentedState<ITimelineItem[]>([]);
  const shallShowTimeline = useMemo(() => timelineItems.length > 0, [timelineItems.length]);

  const [notesSortingOrder, setNotesSortingOrder] = useState<INotesSortingOrder>('descending');
  useEffect(() => {
    // eslint-disable-next-line react-hooks/immutability
    setNewNotesQueue([]);
    setNotes([]);
    // eslint-disable-next-line react-hooks/immutability
    getNotes('sort');
  }, [notesSortingOrder]);

  const [newNotesQueue, setNewNotesQueue] = useState<INote[]>([]);
  const isNewNotesInQueue = useMemo(() => newNotesQueue.length > 0, [newNotesQueue.length]);
  const { inView, ref: rtcMetadataRef } = useInView({
    rootMargin: '300px',
    threshold: 0,
  });
  const shallShowNewNotesButton = useMemo(() => !inView && isNewNotesInQueue && notesSortingOrder === 'descending', [inView, isNewNotesInQueue, notesSortingOrder]);
  const flushNewNotesQueue = ({ shallScrollToLastNote = true } = {}) => {
    // This function should be used only for notesSortingOrder === 'descending'!
    newNotesQueue.forEach((note) => {
      if (note.important) {
        setTimelineItems((prev) => [convertNoteToTimelineItem(note), ...prev]);
      }

      const newNote = { ...note, _firstShow: true };
      // After 1 second, _firstShow is set to false (using state mutation to prevent rerendering),
      // so the note will not be treated as "just added" anymore
      setTimeout(() => { newNote._firstShow = false; }, 1000);

      setNotes((prev) => [newNote, ...prev]);
    });

    if (shallScrollToLastNote) {
      const lastNewNote = newNotesQueue.at(-1);
      if (lastNewNote) {
        scrollToElementWithOffset({ id: `note-${lastNewNote.id}`, offset: -100 });
      }
    }

    setNewNotesQueue([]);
  };
  useEffect(() => {
    if (inView && isNewNotesInQueue && notesSortingOrder === 'descending') {
      flushNewNotesQueue({ shallScrollToLastNote: false });
    }
  }, [inView, isNewNotesInQueue, notesSortingOrder]);

  /**
   * @TODO
   * Data state should be moved to global state management like Redux / Zustand
   */
  const [websocketMatchHeader, setWebsocketMatchHeader] = useState<IMatchHeader>();
  useEffect(() => {
    const ws = new WebSocket(WEBSOCKET_URL);

    const wsOpen = () => {
      const connectFrame = WSFrame('CONNECT', { 'accept-version': '1.1,1.0', 'heart-beat': '10000,10000' });
      ws.send(connectFrame.toString());

      const subscribeFrame = WSFrame('SUBSCRIBE', { destination: '/topic/ws-live-gazeta/' + xx, id: 'sub-0' });
      ws.send(subscribeFrame.toString());

      const subscribeFrameSB = WSFrame('SUBSCRIBE', { destination: '/topic/ws-live/' + xx, id: 'sub-1' });
      ws.send(subscribeFrameSB.toString());
    };

    const wsError = () => {
      console.error('RTC: error web socket');
    };

    const isScoreboardMessage = (noteData: INote | IScoreboard): noteData is IScoreboard => isSportLive && 'matchHeaders' in noteData;
    const isGazetaLiveMessage = (noteData: INote | IScoreboard): noteData is INote => !('htmlDesktop' in noteData);
    // const isWyborczaLiveMessage = (noteData: IMatchHeader | INote): noteData is INote => 'htmlDesktop' in noteData;

    const wsMessage = (event: MessageEvent) => {
      const noteData = prepareData(event.data);

      if (noteData) {
        switch (true) {
          case isScoreboardMessage(noteData):
            setWebsocketMatchHeader(noteData.matchHeaders[0]);
            break;
          case isGazetaLiveMessage(noteData):
            if (noteData.status === 'EDIT') {
              const oldNote = findNote<INote>(notesReference.current, noteData);
              const newNote = { ...oldNote, ...noteData };

              if (oldNote && newNote) {
                setNotes((prev) => updateNotes(prev, newNote));
              }

              if (oldNote?.important) {
                if (newNote.important) {
                  const oldTimelineItem = findNote<ITimelineItem>(timelineItemsReference.current, noteData);
                  const newTimelineItem = convertNoteToTimelineItem(noteData);

                  if (oldTimelineItem && newTimelineItem) {
                    setTimelineItems((prev) => updateNotes(prev, { ...oldTimelineItem, ...newTimelineItem }));
                  }
                } else {
                  setTimelineItems((prev) => deleteNote(prev, oldNote.id));
                }
              } else if (newNote.important) {
                const newTimelineItem = convertNoteToTimelineItem(newNote);
                setTimelineItems((prev) => [newTimelineItem, ...prev]);
              }
            } else if (noteData.status === 'DELETE') {
              setNotes((prev) => deleteNote(prev, noteData.id));
              setTimelineItems((prev) => deleteNote(prev, noteData.id));
            } else {
              setNewNotesQueue((prev) => [...prev, noteData]);
            }
            break;
        }
      }
    };

    ws.addEventListener('open', wsOpen);
    ws.addEventListener('error', wsError);
    ws.addEventListener('message', wsMessage);

    return () => {
      ws.removeEventListener('open', wsOpen);
      ws.removeEventListener('error', wsError);
      ws.removeEventListener('message', wsMessage);
      ws.close();
    };
  }, []);

  const getNotes = async (mode: 'more' | 'sort'): Promise<void> => {
    try {
      setShallShowLoader(true);
      setShallLoadMoreNotes(false);

      const fetchNotesDataCallbackMap: Record<INotesSortingOrder, typeof fetchNotesAfterData | typeof fetchNotesBeforeData> = {
        ascending: fetchNotesAfterData,
        descending: fetchNotesBeforeData,
      };

      const response = await fetchNotesDataCallbackMap[notesSortingOrder]({
        articleId: xx,
        date: notesReference.current.length > 0 && mode !== 'sort' ? new Date(notesReference.current.at(-1)!.publicationDate) : undefined,
        limit: NOTES_LIMIT_PER_REQUEST,
      });

      setNotes((prev) => [...prev, ...response.notes]);
      setTimelineItems(response.timeline);
      setShallLoadMoreNotes(!(response.notes.length < NOTES_LIMIT_PER_REQUEST));
    } catch (e) {
      setShallLoadMoreNotes(false);
    } finally {
      setShallShowLoader(false);
    }
  };

  const handleButtonShowNewNotesClick = () => {
    flushNewNotesQueue();
  };
  const handleTimelineItemClick = async (id: string) => {
    const note = notesReference.current.find(n => n.id === id);

    if (note) {
      scrollToElementWithOffset({ id: `note-${note.id}`, offset: -100 });
    } else {
      await getNotes('more');
      await handleTimelineItemClick(id);
    }
  };
  const handleButtonSortClick = () => {
    setNotesSortingOrder((prev) => (prev === 'ascending' ? 'descending' : 'ascending'));
  };
  const handleButtonGetMoreNotesClick = () => {
    getNotes('more');
  };

  return (
    <>
      {shallShowNewNotesButton ? <ButtonShowNewNotes onClick={handleButtonShowNewNotesClick} quantity={newNotesQueue.length} /> : null}
      <div className={styles['rtc-wrapper']}>
        {isSportLive ? <LiveSportScoreboard matchHeader={websocketMatchHeader} xx={xx} /> : null}
        {shallShowTimeline ? <Timeline items={timelineItems} onItemClick={handleTimelineItemClick} /> : null}

        <div className={styles.notes} data-e='notes'>
          <div className={styles.notes__top} ref={rtcMetadataRef}>
            <div className={styles.notes__metadata}>
              <div className={styles.notes__title}>
                Relacja na żywo
              </div>
              <LiveStatusLabel />
              <ButtonSort notesSortingOrder={notesSortingOrder} onButtonSortClick={handleButtonSortClick} />
            </div>
            <AdTop />
          </div>
          <div className={styles['notes-wrapper']}>
            {notes.map((note, i) => {
              const shallShowAdvert = (i + 1) % SHOW_ADVERT_EVERY_NUMBER_OF_NOTES === 0;
              return (
                <Fragment key={`note-${note.id}-${note._updateTimestamp || 0}`}>
                  <Note note={note} />
                  {shallShowAdvert ? <Ad adsIndex={Math.floor((i + 1) / SHOW_ADVERT_EVERY_NUMBER_OF_NOTES)} /> : null}
                </Fragment>
              );
            })}
          </div>
          {shallShowLoader ? <Loader /> : null}
        </div>
      </div>
      {shallLoadMoreNotes ? <ButtonLoadMoreNotes onButtonGetMoreNotesClick={handleButtonGetMoreNotesClick} /> : null}
    </>
  );
}
